/**
 * CSV Parsing utilities for roster upload using papaparse
 */

import Papa from 'papaparse';

import {
  ALL_VALID_CSV_HEADERS,
  CSV_HEADER_GROUPS,
  CSV_HEADERS,
} from './constants';

/**
 * @typedef {Object} CSVRow
 * @property {string} npi
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} last4
 * @property {string} email
 * @property {number} rowNumber - Original row number in CSV (1-based, excluding header)
 */

/**
 * @typedef {Object} ParseError
 * @property {number} row - Row number where error occurred
 * @property {string} message - Error message
 */

/**
 * @typedef {Object} ParseResult
 * @property {CSVRow[]} rows - Parsed CSV rows
 * @property {ParseError[]} errors - Parsing errors
 * @property {string|null} headerError - Header validation error message, if any
 * @property {number} uniqueNpiCount - Total count of unique NPIs found in CSV
 * @property {number} rowCount - Total count of meaningful (non-empty) data rows found in CSV (includes duplicates)
 * @property {number[]} duplicateRowNumbers - Row numbers (1-based, including header) for duplicate provider rows (2nd+ occurrences only)
 */

/**
 * Read CSV file and parse it using papaparse
 * @param {File} file - CSV file to parse
 * @returns {Promise<ParseResult>}
 */
export const readAndParseCSV = async (file) => {
  return new Promise((resolve, reject) => {
    const rows = [];
    const errors = [];

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy', // More aggressive empty line skipping
      transformHeader: (header) => header.trim(),
      transform: (value) => {
        // Trim values and remove trailing commas
        if (!value) return '';
        const cleaned = String(value)
          .trim()
          .replace(/^,+|,+$/g, '');
        return cleaned;
      },
      complete: (results) => {
        // Filter out empty rows from results.data before processing
        // papaparse sometimes includes rows with all empty/whitespace values
        const filteredData = (results.data || []).filter((row) => {
          return Object.values(row).some((val) => {
            if (!val) return false;
            const cleaned = String(val)
              .trim()
              .replace(/^,+|,+$/g, '');
            return cleaned.length > 0;
          });
        });

        // Ignore all papaparse parsing errors - we'll validate rows ourselves
        // These are often false positives from empty rows or formatting issues

        // Validate headers - check for missing or invalid headers
        let headerError = null;
        if (results.meta.fields && results.meta.fields.length > 0) {
          const headers = results.meta.fields.map((h) =>
            h.toLowerCase().trim(),
          );
          const missingHeaders = CSV_HEADER_GROUPS.filter(
            (group) => !group.some((alias) => headers.includes(alias)),
          ).map((group) => group[0]);

          // Check for invalid headers (headers that don't match expected ones)
          // Exclude empty strings and whitespace-only headers
          const invalidHeaders = headers.filter(
            (header) =>
              header?.trim() && !ALL_VALID_CSV_HEADERS.includes(header),
          );

          if (missingHeaders.length > 0 || invalidHeaders.length > 0) {
            const errorParts = [];
            if (missingHeaders.length > 0) {
              errorParts.push(
                `Missing required headers: ${missingHeaders.join(', ')}`,
              );
            }
            if (invalidHeaders.length > 0) {
              errorParts.push(
                `Invalid headers found: ${invalidHeaders.join(', ')}`,
              );
            }
            headerError = errorParts.join('. ');
          }
        } else {
          // No headers found at all
          headerError =
            'CSV file is missing headers. Please download the template and use the correct format.';
        }

        // Process data rows - only process rows that have at least one non-empty field
        // Use filteredData instead of results.data to skip empty rows
        let actualRowIndex = 0;
        let meaningfulRowCount = 0;
        // Track seen NPIs to prevent duplicates (check before validation)
        const seenNpis = new Set();
        const duplicateRowNumbers = [];

        filteredData.forEach((row) => {
          actualRowIndex++;
          const rowNumber = actualRowIndex + 1; // +1 because header is row 1, actualRowIndex starts at 0

          // Clean and check for meaningful data
          const cleanValue = (val) => {
            if (!val) return '';
            const cleaned = String(val)
              .trim()
              .replace(/^,+|,+$/g, '')
              .trim();
            return cleaned;
          };

          // Extract NPI first to check for duplicates early
          // Use CSV_HEADERS constants to ensure consistency
          const npi = cleanValue(row[CSV_HEADERS[0]] || row.npi);

          // Extract other values
          const firstName = cleanValue(
            row[CSV_HEADERS[1]] || row['provider first name'],
          );
          const lastName = cleanValue(
            row[CSV_HEADERS[2]] || row['provider last name'],
          );
          const last4 = cleanValue(
            row[CSV_HEADERS[3]] ||
              row['Last 4 of DEA or License'] ||
              row['last 4 of dea or license'] ||
              row['Last 1–4 of DEA or License'] ||
              row['last 1–4 of dea or license'] ||
              row['last 1-4 of dea or license'],
          );
          const email = cleanValue(
            row[CSV_HEADERS[4]] || row["provider's email address"],
          );

          // Check if row has ANY meaningful data (at least one field with content)
          const hasData =
            npi.length > 0 ||
            firstName.length > 0 ||
            lastName.length > 0 ||
            last4.length > 0 ||
            email.length > 0;

          // Skip completely empty rows or rows with only commas/whitespace
          if (!hasData) {
            return;
          }

          // Count meaningful rows BEFORE de-duping so duplicates still count toward row limits.
          meaningfulRowCount += 1;

          // Skip duplicate NPIs for downstream validation/error reporting.
          if (npi && seenNpis.has(npi)) {
            // Record the duplicate row number (we intentionally do NOT record the first occurrence)
            duplicateRowNumbers.push(rowNumber);
            return;
          }

          // Track this NPI for downstream processing.
          if (npi) {
            seenNpis.add(npi);
          }

          // Check for required fields (values already extracted above)
          // Use CSV_HEADERS constants to ensure consistency
          const missingFields = [];
          if (!npi) missingFields.push(CSV_HEADERS[0]);
          if (!firstName) missingFields.push(CSV_HEADERS[1]);
          if (!lastName) missingFields.push(CSV_HEADERS[2]);
          if (!last4) missingFields.push(CSV_HEADERS[3]);

          if (missingFields.length > 0) {
            errors.push({
              row: rowNumber,
              message: `Missing required fields: ${missingFields.join(', ')}`,
              // Include firstName and lastName if available (even if other fields are missing)
              // This allows UI to display provider name instead of just "CSV Row #X"
              firstName: firstName || '',
              lastName: lastName || '',
              // Include NPI if available so we can fetch provider details even when other fields are missing
              npi: npi || '',
            });
            return;
          }

          rows.push({
            npi,
            firstName,
            lastName,
            last4,
            email,
            rowNumber,
          });
        });

        resolve({
          rows,
          errors,
          headerError,
          uniqueNpiCount: seenNpis.size,
          rowCount: meaningfulRowCount,
          duplicateRowNumbers,
        });
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      },
    });
  });
};
